import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { Button, Card, Logo } from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { PILOT_CONTEXT_KEY, fetchPilotContext } from '@wundo/client/pilot.ts';
import { m } from '@/paraglide/messages.js';

/**
 * The first screen, and it asks for nothing.
 *
 * Setting Wundo up used to open on the calendar — a permission request to
 * somebody who had not yet been told what Wundo is. So the wizard now says
 * hello first and explains itself second, and only then asks. Nothing here is
 * stored and nothing can go wrong: one sentence, and the way on.
 */
export const Route = createFileRoute('/onboarding/welcome')({
  component: OnboardingWelcomePage,
});

export function OnboardingWelcomePage() {
  const navigate = useNavigate();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  /*
   * A pilot member is told what the coming weeks hold instead of what Wundo is
   * in general, and goes on to the three-screen wizard rather than the calendar.
   */
  const { data: pilot } = useQuery({
    queryKey: PILOT_CONTEXT_KEY,
    queryFn: fetchPilotContext,
  });

  return (
    <Card
      padding='none'
      className='mx-auto w-full max-w-130 px-11 pt-11 pb-9 text-center max-mobile:px-6 max-mobile:pt-8'
    >
      <div className='flex justify-center'>
        <Logo />
      </div>

      {/*
        The name is a greeting, not a fact the screen depends on. A user whose
        first name we never got is greeted anyway — "Fijn dat je er bent, ."
        is worse than no name at all.
      */}
      <h1 className='mt-6 mb-3 text-xl font-medium tracking-tight'>
        {user?.first_name
          ? m.onboarding_welcome_title({ name: user.first_name })
          : m.onboarding_welcome_title_short()}
      </h1>
      <p className='text-md text-ink-muted'>
        {pilot
          ? m.onboarding_pilot_welcome_subtitle({ weeks: pilot.weeks })
          : m.onboarding_welcome_subtitle()}
      </p>

      <div className='mt-8 flex justify-center'>
        <Button
          variant='accent'
          shape='block'
          size='lg'
          onClick={() =>
            void navigate({
              to: pilot ? '/onboarding/how-it-works' : '/onboarding/what-you-get',
            })
          }
        >
          {m.onboarding_welcome_continue()}
          <ArrowRight aria-hidden='true' className='size-4' />
        </Button>
      </div>
    </Card>
  );
}
